import React from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import MenuClose from 'assets/icons/MenuClose.png'


interface UndoPackAllocationDialogProps {
  faberToken: string
  handleUndoAllocationDialogClose: () => void
  dialogOpen: boolean
  allocationId: string | null
}

function UndoAllocationPackDIalog({
  faberToken,
  allocationId,
  handleUndoAllocationDialogClose,
  dialogOpen,
}: UndoPackAllocationDialogProps) {
  const queryClient = useQueryClient()
  const { mutate: undoPackAllocation, isLoading: isUndoPackLoading } = useMutation(
    EsimAPI.undoPackAllocation,
    {
      onSuccess: () => {
        toast.success('Pack allocation undo successful')
        queryClient.refetchQueries({ queryKey: ['customer-allocation-packs'] });
      },
      onError: (error: { response?: { data?: { Error?: string; Message?: string } } }) => {
        const errorMessage = error?.response?.data?.Error || error?.response?.data?.Message || 'An unexpected error occurred';
        toast.error(errorMessage);
      },
      onSettled: () => {
        handleUndoAllocationDialogClose()
      },
    }
  )


  const handleUndoAllocation = () => {
    if (allocationId) {
      undoPackAllocation({ allocationId, faberToken })
    } else {
      toast.error('No allocation selected')
    }
  }

  return (
    <Dialog
      open={dialogOpen}
      maxWidth="sm"
      fullWidth={true}
      onClose={handleUndoAllocationDialogClose}
      sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
    >
      <DialogTitle
        sx={{
          fontWeight: '700',
          fontSize: '20px',
          color: '#333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Undo Pack Allocation
        <IconButton
          onClick={handleUndoAllocationDialogClose}
          size="small"
          sx={{
            marginRight: '-8px',
            marginTop: '-8px',
          }}
        >
          <img
            style={{
              width: '20px',
              height: '20px',
            }}
            src={MenuClose}
            alt="Close"
          />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            marginTop: 2,
            marginBottom: 2,
            color: '#555',
          }}
        >
          Are you sure you want to undo this pack allocation? This action cannot be reversed.
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          padding: '16px',
          display: 'flex',
        }}
      >
        <LoadingButton variant='text' onClick={handleUndoAllocationDialogClose}>
          Cancel
        </LoadingButton>

        <LoadingButton
          variant="contained"
          onClick={handleUndoAllocation}
          loading={isUndoPackLoading}
          loadingIndicator={<CircularProgress size={24} />}
          sx={{
            paddingX: 4,
            fontWeight: 'bold',
          }}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default UndoAllocationPackDIalog
